/**
 * @file: useDeliveryCheckFlows.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { AppNavParams } from '@euler/app/Routes';
import {
  CompletionCheckIcon,
  SamplingCheckIcon,
} from '@euler/app/components/icons';
import { TaskNavParams } from '@euler/app/flows/task/TaskFlow';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useViewReportWithTaskContext } from '@euler/app/flows/task/functions/useViewReportWithTaskContext';
import { QualityCheckFlowInfo } from '@euler/app/flows/task/types';
import { StatusColors } from '@euler/components/Colors';
import { useAppLoading } from '@euler/components/loading/Provider';
import { OrgUserRoleHelper } from '@euler/functions/OrgUserRoleHelper';
import { useStoreFeature } from '@euler/functions/entitlements/useStoreFeature';
import { useAppReviewTracking } from '@euler/functions/useAppReviewTracking';
import { useCurrentUserRole } from '@euler/functions/user/useCurrentUserRole';
import { useAlert } from '@euler/functions/ux/useAlert';
import { AnalyticEvents } from '@euler/generated/AnalyticEvents';
import {
  CommonTaskStatus,
  DeliveryCheckSceneType,
  InspectionTaskJobStatus,
  InspectionTaskStatus,
  SamplingCheckResultType,
  StoreFeature,
} from '@euler/model/enum';
import { usePersistFn } from '@euler/utils/hooks';
import { runAfterInteractions } from '@euler/utils/runAfterInteractions';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useMemo } from 'react';

export const useDeliveryCheckFlows = () => {
  const navigation =
    useNavigation<StackNavigationProp<TaskNavParams & AppNavParams>>();

  const role = useCurrentUserRole();

  const {
    taskManager,
    storeConfiguration,
    profile: { store },
    detail: {
      basicInfo,
      deliveryCheck,
      samplingCheck,
      preInspection,
      inspection,
      construction,
    },
  } = useTaskContext();

  const { isIndependentDeliveryCheckEnabled } = store;
  const isSamplingDeliveryCheckEnabled = useStoreFeature(
    StoreFeature.SamplingCheck,
  ).enabled;

  const alert = useAlert();
  const appLoading = useAppLoading();
  const trackAppReview = useAppReviewTracking();
  const viewReport = useViewReportWithTaskContext();

  const finishConstruction = useCallback(
    async (options?: { onSuccess: () => Promise<void> }) => {
      await appLoading.showWithAction(async () => {
        await taskManager.constructionManager.finish();
        if (options?.onSuccess != null) {
          await options.onSuccess();
        }
        trackAppReview();
      });
    },
    [appLoading, taskManager.constructionManager, trackAppReview],
  );

  const flows = useMemo<QualityCheckFlowInfo[]>(() => {
    const result: QualityCheckFlowInfo[] = [];
    if (
      isIndependentDeliveryCheckEnabled ||
      deliveryCheck.status === CommonTaskStatus.Finished
    ) {
      result.push({
        name: isIndependentDeliveryCheckEnabled ? '竣工检验' : '交车质检',
        target: deliveryCheck,
        type: DeliveryCheckSceneType.CompletionCheck,
        icon: <CompletionCheckIcon />,
        description: '由班组完成的完工交车前竣工质量检验',
        status: deliveryCheck.status,
        startedAt: deliveryCheck.startedAt,
        finishedAt: deliveryCheck.finishedAt,
        author:
          deliveryCheck.status === CommonTaskStatus.Finished
            ? deliveryCheck.finishedBy
            : deliveryCheck.startedBy,
        timestamp:
          deliveryCheck.status === CommonTaskStatus.Finished
            ? deliveryCheck.finishedAt
            : deliveryCheck.startedAt,
      });
    }
    if (
      isSamplingDeliveryCheckEnabled &&
      (samplingCheck.status !== CommonTaskStatus.Pending ||
        isIndependentDeliveryCheckEnabled ||
        (basicInfo.status === InspectionTaskStatus.Finished &&
          storeConfiguration.allowSamplingCheckAfterFinish))
    ) {
      result.push({
        name: '完工抽检',
        target: samplingCheck,
        disabled: deliveryCheck?.status !== CommonTaskStatus.Finished,
        type: DeliveryCheckSceneType.SamplingCheck,
        icon: <SamplingCheckIcon />,
        description: '由质检组进行的完工交车前的抽检工作',
        status: samplingCheck?.status ?? CommonTaskStatus.Pending,
        startedAt: samplingCheck?.startedAt,
        finishedAt: samplingCheck?.finishedAt,
        author:
          samplingCheck?.status === CommonTaskStatus.Finished
            ? samplingCheck?.finishedBy
            : samplingCheck?.startedBy,
        timestamp:
          samplingCheck?.status === CommonTaskStatus.Finished
            ? samplingCheck?.finishedAt
            : samplingCheck?.startedAt,
        tip:
          samplingCheck?.status === CommonTaskStatus.Finished
            ? samplingCheck.resultType === SamplingCheckResultType.Failed
              ? '抽检不合格，禁止交车'
              : '检验合格，允许交车'
            : deliveryCheck.status !== CommonTaskStatus.Finished
            ? '需先完成竣工检验'
            : '',
        tipColor:
          samplingCheck?.status === CommonTaskStatus.Finished
            ? samplingCheck.resultType === SamplingCheckResultType.Failed
              ? StatusColors.Danger
              : StatusColors.Success
            : undefined,
      });
    }
    return result;
  }, [
    basicInfo.status,
    deliveryCheck,
    isIndependentDeliveryCheckEnabled,
    isSamplingDeliveryCheckEnabled,
    samplingCheck,
    storeConfiguration.allowSamplingCheckAfterFinish,
  ]);

  const onCompletionCheckFlowPress = usePersistFn(() => {
    if (deliveryCheck.status === CommonTaskStatus.Finished) {
      viewReport('deliveryCheckReport', {
        title: store.isIndependentDeliveryCheckEnabled
          ? '竣工检验报告'
          : undefined,
        canEdit: () => store.isIndependentDeliveryCheckEnabled,
        canFinish: () => !store.isIndependentDeliveryCheckEnabled,
      });
      return;
    }

    if (storeConfiguration.restrictedRolesForPreDeliveryCheck?.length) {
      const roleHelper = new OrgUserRoleHelper(role);
      if (
        !roleHelper.matchesAny(
          storeConfiguration.restrictedRolesForPreDeliveryCheck,
        )
      ) {
        void alert({ title: '对不起，您当前没有权限进行竣工检验!' });
        return;
      }
    }

    if (
      preInspection.status === CommonTaskStatus.InProgress ||
      inspection.status === CommonTaskStatus.InProgress
    ) {
      if (construction.status === CommonTaskStatus.InProgress) {
        void alert({
          title: '提示',
          message: '请先完成检测和施工项目并推送报告，然后进行竣工检验!',
        });
      } else if (
        preInspection.status === CommonTaskStatus.InProgress &&
        inspection.status === CommonTaskStatus.InProgress
      ) {
        void alert({
          title: '提示',
          message: '请先完成预检和全车检测项目并推送报告，然后进行竣工检验!',
        });
      } else if (preInspection.status === CommonTaskStatus.InProgress) {
        void alert({
          title: '提示',
          message: '请先完成预检项目并推送报告，然后进行竣工检验!',
        });
      } else {
        void alert({
          title: '提示',
          message: '请先完成全车检测项目并推送报告，然后进行竣工检验',
        });
      }
      return;
    }

    if (construction.status === CommonTaskStatus.InProgress) {
      if (
        construction.jobs.some(
          x => x.status !== InspectionTaskJobStatus.Finished,
        )
      ) {
        void alert({
          title: '提示',
          message: '请先完成施工项目并推送报告，然后进行竣工检验!',
        });
        return;
      }

      void alert({
        title: '尚未完成施工',
        message: '是否立即完成施工并推送报告?',
        buttons: [
          {
            text: '取消',
            style: 'cancel',
          },
          {
            text: '预览施工报告',
            style: 'default',
            onPress: () => {
              AnalyticEvents.default.taskDeliveryCheckAlertPreviewButtonPressed();
              viewReport('constructionReport', {
                onFinish: async () => {
                  await finishConstruction();
                },
                async onFinishSuccess() {
                  await taskManager.deliveryCheckManager.begin();
                  await taskManager.deliveryCheckManager.prepare();
                  navigation.replace('_deliveryCheck', {});
                },
              });
            },
          },
          {
            text: '直接推送并开始竣工检验',
            style: 'destructive',
            onPress: async () => {
              AnalyticEvents.default.taskDeliveryCheckAlertFinishButtonPressed();
              await finishConstruction({
                onSuccess: async () => {
                  await taskManager.deliveryCheckManager.begin();
                  await taskManager.deliveryCheckManager.prepare();
                  navigation.push('_deliveryCheck', {});
                },
              });
            },
          },
        ],
      });

      return;
    }

    runAfterInteractions(async () => {
      await appLoading.showWithAction(async () => {
        await taskManager.deliveryCheckManager.begin();
        await taskManager.deliveryCheckManager.prepare();
        navigation.push('_deliveryCheck', {});
      });
    });
  });

  const onSamplingCheckFlowPress = usePersistFn(() => {
    if (samplingCheck.status === CommonTaskStatus.Finished) {
      viewReport('samplingCheckReport', {});
      return;
    }

    if (storeConfiguration.restrictedRolesForPreSamplingDeliveryCheck?.length) {
      const roleHelper = new OrgUserRoleHelper(role);
      if (
        !roleHelper.matchesAny(
          storeConfiguration.restrictedRolesForPreSamplingDeliveryCheck,
        )
      ) {
        void alert({ title: '对不起，您当前没有权限进行完工抽检!' });
        return;
      }
    }

    if (deliveryCheck.status === CommonTaskStatus.Pending) {
      void alert({ title: '提示', message: '请先进行竣工检验再开始完工抽检!' });
      return;
    }
    if (
      deliveryCheck.status === CommonTaskStatus.InProgress &&
      samplingCheck.status === CommonTaskStatus.Pending
    ) {
      void alert({ title: '提示', message: '请先完成竣工检验再开始完工抽检!' });
      return;
    }
    runAfterInteractions(async () => {
      await appLoading.showWithAction(async () => {
        await taskManager.samplingCheckManager.begin();
        await taskManager.samplingCheckManager.prepare();
        navigation.push('_samplingCheck', {});
      });
    });
  });

  const onDeliveryCheckFlowPress = useCallback(
    (flow: QualityCheckFlowInfo) => {
      if (flow.type === DeliveryCheckSceneType.CompletionCheck) {
        onCompletionCheckFlowPress();
      } else if (flow.type === DeliveryCheckSceneType.SamplingCheck) {
        onSamplingCheckFlowPress();
      }
    },
    [onCompletionCheckFlowPress, onSamplingCheckFlowPress],
  );

  return { flows, onDeliveryCheckFlowPress };
};
